import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import {connect} from 'react-redux';
import MenuItem from '@material-ui/core/MenuItem';
import {toWords} from 'number-to-words';
import moment from 'moment';
import ContractLineItem from '../components/contracts/ContractLineItem';
import ContractInfoSidePanel from '../components/contracts/ContractInfoSidePanel';
import ContractSignContainer from '../components/ContractSignContainer';
import {
    agreementSignedEvent,
    deliverySlipSignedEvent, devicesDeliveredEvent,
    getAllContracts,
    getCompanyContracts,
    getCustomerContracts, signedDeliverySlipUploadedEvent,
    updateContract,
} from '../redux/actions/contractsActions';
import {createContract, viewContract} from '../api/network/contractsService';
import ContractDeliveryDialog from '../components/contracts/ContractDeliveryDialog';
import {
    CONTRACT_KEYS,
    contractColorsCodes,
    CONTRACT_STATUS,
    statusObjects,
    COMMITMENT_LETTER_STATUS,
    PROMISSORY_NOTE_STATUS
} from '../utils/contracts';
import {getDocument, uploadDocument} from '../api/network/documentService';
import {
    addCustomerDocument,
    getCustomerDocument,
    updateCommitment,
    updatePromissory
} from '../api/network/businessCustomerService';
import {DOCUMENT_TYPE} from '../const/documents';
import ContractsLineItem from '../components/contracts/ContractsLineItem';
import MenuActions from '../components/common/MenuActions';
import {getDurationInMonth} from '../utils';
import {convertNumToCurrencyWords, convertNumToPercentageWords, currencyFormatter} from '../utils/formatter';
import ContractsLineItemHeadings from '../components/contracts/ContractsLineItemHeadings';
import CustomSelection from '../components/common/CustomSelection';
import {roles} from '../utils/auth';
import endpoints from '../endpoints';
import CustomerContractDeliveryDialog from '../components/contracts/CustomerContractDeliveryDialog';
import DocumentSignContainer from '../components/DocumentSignContainer';

const PageWrapper = styled.div`
    display: flex;
    overflow: hidden;
    height: calc(100vh - var(--header-height));
    //height: 100px;
`;

const PageContent = styled.div`
    padding-left: 48px;
    padding-top: 84px;
    padding-bottom: 84px;
    flex: 1;
    overflow-y: scroll;
`;

const ContractsWrapper = styled.div`
    display: grid;
    grid-auto-flow: row;
    grid-row-gap: 48px;
`;

const FilterWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    margin: 0 0 13px 16px;
`;

function CustomerContracts(props) {
    const {
        auth,
        user,
        contracts,
        getAllContracts,
        getCompanyContracts,
        getCustomerContracts,
        updateContract,
        agreementSignedEvent
    } = props;

    //--------------------------- Side Effects

    useEffect(() => {
        // Fetch the contracts for the customer

        // TODO: Will prevent table updates from being immediately shown as the assumption here is that the contracts dataset is coming from an empty state
        const canGetCustomers = user && user.companyId && user.customerId && !contracts.length;
        if (canGetCustomers)
            getCustomerContracts(user.companyId, user.customerId);
        //console.log("Contracts: "+contracts);
    }, [user]);

    //--------------------------- States

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [signContract, setSignContract] = useState(false);
    const [selectedContract, setSelectedContract] = useState(null);
    const [selectedContractIds, setSelectedContractIds] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [invoiceDocumentFile, setInvoiceDocument] = useState(null);
    const [signatureRef, setSignatureRef] = useState();

    // Document View States
    const [documentKey, setDocumentKey] = useState('');
    const [documentParams, setDocumentParams] = useState({});

    const [contractFilter, setContractFilter] = useState('default');
    const [showDocumentPad, enableDocumentPad] = useState(false);

    /**
     *  sorting state is used to determine what key currently we are sorting by.
     *  @ key - parameter by with we are filtering the contract by.
     *  @ ascending - if true the sorting order is ascending else the order is descending.
     */
    const [sort, setSort] = useState({key: 'count', ascending: false});

    //--------------------------- Handler functions
    const openContractInfoPanel = contract => {
        setSelectedContract(contract);
        setDrawerOpen(true);
    };

    const handleOnDrawerClose = () => {
        setDrawerOpen(false);
        setSelectedContract(null);
    };

    const handleOnViewContractClick = () => {
        setDrawerOpen(false);
    };

    const handleSignContractClick = () => {
        //setDrawerOpen(false);
        setSignContract(true);
    };

    const handleCommitmentSignature = (documentId, contract) => {
        enableDocumentPad(false);
        //const {status} = contract;
        const {companyId} = contract;
        const {customerId} = contract;
        const repId = contract.representativeId;

        const params = {
            documentId,
            status: COMMITMENT_LETTER_STATUS.CUSTOMER_SIGNED
        };

        // update commitment letter status
        updateCommitment(companyId, customerId, repId, contract.id, contract.commitmentLetter.id, params)
            .then(() => {
                const updatedContract = {
                    ...contract,
                    commitmentLetter: {...contract.commitmentLetter, ...params}
                };
                agreementSignedEvent(updatedContract);
            });

        console.info(`commitment ${Object.values(contract.commitmentLetter)}`);
    };

    const handlePromissorySignature = (documentId, contract) => {
        //const {status} = contract;
        const {companyId} = contract;
        const {customerId} = contract;
        const repId = contract.representativeId;

        const updatedPromissoryNoteParams = {
            documentId,
            status: PROMISSORY_NOTE_STATUS.CUSTOMER_SIGNED
        };

        // update promissory note status
        updatePromissory(companyId, customerId, repId, contract.id, contract.promissoryNote.id, updatedPromissoryNoteParams)
            .then(() => {
                const updatedContract = {
                    ...contract,
                    promissoryNote: {...contract.promissoryNote, ...updatedPromissoryNoteParams}
                };
                agreementSignedEvent(updatedContract);
            });
    };

    const convertDay = date => {
        const day = new Date(date);
        return day.getDate();
    };
    const convertMonth = date => {
        const day = new Date(date);
        return day.getMonth();
    };
    const convertYear = date => {
        const day = new Date(date);
        return day.getFullYear();
    };

    const handleSignCommitmentClick = (selectedContract = {}) => {
        // set document states
        setDocumentKey(CONTRACT_KEYS.COMMITMENT_LETTER);

        const agreementDate = moment();
        const contractStartDate = moment(selectedContract.startDateOfContract);
        const contractEndDate = moment(selectedContract.endDateOFContract).add(1, 'months');
        setDocumentParams({
            cfname: selectedContract.representativeName.split(' ')[0] || '',
            clname: selectedContract.representativeName.split(' ')[1] || '',
            cfname_initial: selectedContract.representativeName.split(' ')[0][0] || '',
            clname_initial: selectedContract.representativeName.split(' ')[1][0] || '',
            caddress: selectedContract.companyAddress || '',
            agday: agreementDate.format('D') || '',
            agmonth: agreementDate.format('MMMM') || '',
            agyear: agreementDate.format('YYYY') || '',
            agday_end: contractEndDate.format('D') || '',
            agmonth_end: contractEndDate.format('MMMM') || '',
            agyear_end: contractEndDate.format('YYYY') || '',
            num_installments: selectedContract.numInstallments || '',
            loan_purpose: selectedContract.loanPurpose || '',
            frequency_installments: selectedContract.installmentFrequency?.toUpperCase() || '',
            loan_principal_words_figures: `${convertNumToCurrencyWords(selectedContract.loanPrinciple)} ($${currencyFormatter(selectedContract.loanPrinciple)})` || '',
            amount_interest_words_figures: `${convertNumToCurrencyWords(selectedContract.interestAmount)} ($${currencyFormatter(selectedContract.interestAmount)})` || '',
            installment_amount_words_figures: `${convertNumToCurrencyWords(selectedContract.monthlyPayment)} ($${currencyFormatter(selectedContract.monthlyPayment)})` || '',
            interest_rate_words_figures: `${convertNumToPercentageWords(selectedContract.interestsRate)} (${selectedContract.interestsRate}%)` || '',
            loan_term_words_figures: `${toWords(selectedContract.term).toUpperCase()} (${selectedContract.term}) Months` || '',
            witness_address: '73 Harbour Street, Kingston'
        });

        //show commit signature pad.
        setSelectedContract(selectedContract);
        enableDocumentPad(true);
    };

    const handlePromissoryNoteClick = selectedContract => {
        //show commit signature pad.
        setDocumentKey(CONTRACT_KEYS.PROMISSORY_NOTE);

        const agreementDate = moment();
        const contractStartDate = moment(selectedContract.startDateOfContract);
        const installmentStartDate = contractStartDate.add(1, 'months');
        const contractEndDate = moment(selectedContract.endDateOFContract).add(1, 'months');
        const totalAmount = Number(selectedContract.loanPrinciple) + Number(selectedContract.interestAmount);
        const installmentValue = currencyFormatter(totalAmount / selectedContract.numInstallments);
        setDocumentParams({
            cfname: selectedContract.representativeName.split(' ')[0],
            clname: selectedContract.representativeName.split(' ')[1],
            amount: `${currencyFormatter(totalAmount)}`,
            principal: `${currencyFormatter(selectedContract.loanPrinciple)}`,
            interest: `${currencyFormatter(selectedContract.interestAmount)}`,
            installment: installmentValue,
            installment_interest_rate: selectedContract.interestsRate,
            installments_amount: selectedContract.numInstallments,
            agday: agreementDate.format('D'),
            agmonth: agreementDate.format('MMMM'),
            agyear: agreementDate.format('YYYY'),
            agday_first_installment: installmentStartDate.format('D'),
            agmonth_first_installment: installmentStartDate.format('MMMM'),
            agyear_first_installment: installmentStartDate.format('YYYY'),
            agday_end: contractEndDate.format('D'),
            agmonth_end: contractEndDate.format('MMMM'),
            agyear_end: contractEndDate.format('YYYY'),
            witness_name: '',
            witness_address: '',
            witness_occupation: ''
        });

        setSelectedContract(selectedContract);
        enableDocumentPad(true);
    };

    // const handleCommitLetterSign = (documentId) => {
    //     //show commit signature pad.
    //     setDocumentKey(CONTRACT_KEYS.COMMITMENT_LETTER);
    //     setDocumentParams({}); // TODO get relevant params for document;

    //     setSelectedContract(documentId);
    //     setCommitmentLetterPad(true);
    // };

    const handleReadyForDeliveryClick = contract => {
        const {status} = contract;
        const {companyId} = contract;
        const {customerId} = contract;
        const repId = contract.representativeId;

        switch (status) {
        case CONTRACT_STATUS.DELIVERED:
            // update contract status
            contract.status = CONTRACT_STATUS.GK_PAID;
            updateContract(companyId, customerId, repId, contract.id, contract);
            break;
        default:
            setDrawerOpen(false);
            setOpenDialog(true);
        }
    };

    const handleDialogClose = () => {
        setOpenDialog(false);
    };

    const handleOnSubmit = (contract, invoiceDocument, signatureImage) => {
        console.log('submit clicked', contract, invoiceDocument, signatureImage);

        // when submit we want to send the contract in invoice signed status
        const contractForUpdate = {...contract};

        const {companyId} = contract;
        const {customerId} = contract;
        const repId = contract.representativeId;
        const contractId = contract.id;

        if (invoiceDocument) {
            // Option for uploading signed document is selected

            const formData = new FormData();
            formData.append('file', invoiceDocument);
            // uploading document to document service
            uploadDocument(formData)
                .then(result => {
                    addCustomerDocument(companyId, customerId, {
                        documentId: result.id,
                        documentTypeId: DOCUMENT_TYPE.Contracts
                    }).then(result => {
                        contract.invoiceDocumentId = result.id;
                        contractForUpdate.status = CONTRACT_STATUS.DELIVERED;
                        updateContract(companyId, customerId, repId, contractId, contractForUpdate, devicesDeliveredEvent(contractForUpdate));
                    }).catch(error => console.log('error', error));
                })
                .catch(error => console.log('error', error));
        } else if (signatureImage) {
            // Option for signing image digitally is selected.
            // todo: upload the image
            contractForUpdate.status = CONTRACT_STATUS.DELIVERED;
            updateContract(companyId, customerId, repId, contractId, contractForUpdate, devicesDeliveredEvent(contractForUpdate));
        } else {
            contractForUpdate.status = CONTRACT_STATUS.DELIVERY_SLIP_SIGNED;
            updateContract(companyId, customerId, repId, contractId, contractForUpdate, deliverySlipSignedEvent(contractForUpdate));
        }

        setInvoiceDocument(null);
        setOpenDialog(false);
    };

    const handleViewContract = contract => {
        getCustomerDocument(contract.companyId, contract.customerId, contract.documentId)
            .then(result => {
                const url = endpoints.getDocument(result.documentId);
                window.open(url, '_blank');
            }).catch(error => console.log('error', error));
    };

    const updatedFilter = e => {
        const {value} = e.target;
        setContractFilter(value);
    };

    const hasContractId = contractId => selectedContractIds.some(item => item === contractId);

    const onCheckboxSelected = e => {
        const {value, checked} = e.target;
        let updatedContracts = [...selectedContractIds];

        if (checked) {
            //insert
            updatedContracts.push(value);
        } else {
            //remove
            updatedContracts = selectedContractIds.filter(item => item !== value);
        }
        setSelectedContractIds(updatedContracts);
    };

    const selectAll = e => {
        const {value, checked, indeterminate} = e.target;
        const updatedContracts = [];

        if (checked || indeterminate) {
            // selectAll
            contracts.forEach(item => updatedContracts.push(item.id));
        }

        setSelectedContractIds(updatedContracts);
    };

    const sortItems = e => {
        const {value} = e.target;

        const updatedSorting = {
            key: value,
            ascending: false
        };

        if (sort && sort.key === value) {
            // already sorting by this key so with reverse the sort order.
            updatedSorting.ascending = !sort.ascending;
        }

        setSort(updatedSorting);
    };

    //--------------------------- Data preparation.

    let contractsToDisplay = contracts;

    // sort data
    if (sort) {
        contractsToDisplay = contractsToDisplay.sort((firstItem, secondItem) => ((sort.ascending) ?
            (`${secondItem[sort.key]}`).localeCompare(firstItem[sort.key]) :
            (`${firstItem[sort.key]}`).localeCompare(secondItem[sort.key])));
    }

    // filter data
    if (contractFilter !== 'default') {
        contractsToDisplay = contractsToDisplay.filter(item => item.status === contractFilter);
    }

    return (
        <PageWrapper>
            <PageContent>

                <FilterWrapper style={{display: 'flex', flexDirection: 'row'}}>
                    <span style={{marginRight: '16px'}}> Filter By </span>
                    <CustomSelection
                        onChange={updatedFilter}
                        value={contractFilter}
                    >
                        <MenuItem value="default"><em>All Contracts</em></MenuItem>
                        <MenuItem value={CONTRACT_STATUS.DELIVERED}>Delivered</MenuItem>
                        <MenuItem value={CONTRACT_STATUS.GK_SIGNED}>Approved Contracts</MenuItem>
                        <MenuItem value={CONTRACT_STATUS.DELIVERY_SLIP_SENT}>In Delivery</MenuItem>
                        <MenuItem value={CONTRACT_STATUS.CUSTOMER_SIGNED}>In Review</MenuItem>
                    </CustomSelection>
                </FilterWrapper>

                <ContractsWrapper>

                    <ContractsLineItemHeadings
                        selectable={true}
                        onSelected={selectAll}
                        onHeadingClick={sortItems}
                        isSelected={contracts.length === selectedContractIds.length}
                        indeterminate={false}
                    />

                    {
                        contractsToDisplay.map(contractElement => {
                            const contractMenuActions = (
                                <MenuActions
                                    render={closeMenu => (
                                        <div>
                                            <MenuItem
                                                onClick={() => {
                                                    handleViewContract(contractElement);
                                                    closeMenu();
                                                }}
                                            >
                                                View Contract
                                            </MenuItem>

                                            <MenuItem
                                                onClick={() => {
                                                    openContractInfoPanel(contractElement);
                                                    closeMenu();
                                                }}
                                            >
                                                View Contract Details
                                            </MenuItem>

                                            {
                                                auth.role === roles.CUSTOMER_REP && contractElement.status === CONTRACT_STATUS.DELIVERY_SLIP_SENT &&
                                                <MenuItem onClick={() => {
                                                    setSelectedContract(contractElement);
                                                    handleReadyForDeliveryClick(contractElement);
                                                    closeMenu();
                                                }}
                                                >
                                                    Upload Delivery Slip
                                                </MenuItem>
                                            }

                                            {
                                                auth?.role === roles.CUSTOMER_REP &&
                                                contractElement.commitmentLetter?.status === COMMITMENT_LETTER_STATUS.GENERATED &&
                                                <MenuItem onClick={() => {
                                                    handleSignCommitmentClick(contractElement);
                                                    closeMenu();
                                                }}
                                                >
                                                    Sign Commitment Letter
                                                </MenuItem>
                                            }

                                            {
                                                auth?.role === roles.CUSTOMER_REP &&
                                                contractElement.promissoryNote?.status === PROMISSORY_NOTE_STATUS.GENERATED &&

                                                <MenuItem onClick={() => {
                                                    handlePromissoryNoteClick(contractElement);
                                                    closeMenu();
                                                }}
                                                >
                                                    Sign Promissory Note
                                                </MenuItem>
                                            }

                                        </div>
                                    )}
                                />
                            );

                            const duration = getDurationInMonth(contractElement.startDateOfContract, contractElement.endDateOFContract);
                            const totalCash = `$ ${currencyFormatter(contractElement.totalCashPrice)}`;
                            const monthlyRate = `$ ${currencyFormatter(contractElement.monthlyPayment)}`;

                            const {status: currentStatus, commitmentLetter = {}, promissoryNote = {}} = contractElement;

                            let status;
                            const statusObject = statusObjects[currentStatus];

                            if (!statusObject) {
                                status = {message: currentStatus, color: contractColorsCodes.Neutral};// Fallback
                            } else {
                                status = statusObject[auth.role] || statusObject;
                            }

                            if (currentStatus === CONTRACT_STATUS.GK_SIGNED) {
                                // Customer status messages based on if the customer signed
                                const documentsToSign = [];
                                if (commitmentLetter?.status === COMMITMENT_LETTER_STATUS.GENERATED) documentsToSign.push('Commitment Letter');
                                if (promissoryNote?.status === COMMITMENT_LETTER_STATUS.GENERATED) documentsToSign.push('Promissory Note');

                                const docsMessage = documentsToSign.length ? documentsToSign.join(' & ') : 'GK approval';
                                status.message = `Awaiting ${docsMessage} signature(s)`;
                            } else if (currentStatus === CONTRACT_STATUS.AGREEMENT_SIGNED) {
                                status.message = 'Agreement Accepted';
                            }

                            return <>
                                <ContractsLineItem
                                    key={contractElement.id}
                                    id={contractElement.id}
                                    number={contractElement.contractNumber}
                                    status={status.message}
                                    monthlyRate={monthlyRate}
                                    totalCashPrice={totalCash}
                                    duration={duration}
                                    color={status.color}
                                    actionsComponent={contractMenuActions}
                                    selectable={true}
                                    isSelected={hasContractId(contractElement.id)}
                                    onSelected={onCheckboxSelected}
                                />
                            </>;
                        })
                    }
                </ContractsWrapper>
            </PageContent>

            <ContractInfoSidePanel
                role={auth.role}
                showPanel={drawerOpen}
                contract={selectedContract || {}}
                onSignContractClick={handleSignContractClick}
                onViewContractClick={handleOnViewContractClick}
                onReadyForDeliveryClick={() => handleReadyForDeliveryClick(selectedContract)}
                onPanelClose={handleOnDrawerClose}
            />

            <ContractSignContainer
                contracts={[selectedContract]}
                onPanelClose={() => setSignContract(false)}
                showPanel={signContract}
            />

            <DocumentSignContainer
                showPanel={showDocumentPad}
                onPanelClose={() => enableDocumentPad(false)}
                documentKey={documentKey}
                documentParams={documentParams}
                onDocumentSign={documentId => {
                    documentKey === 'commitment_letter' ? handleCommitmentSignature(documentId, selectedContract) : handlePromissorySignature(documentId, selectedContract);
                    enableDocumentPad(false);
                }}
                signatureKey="csignature"
            />

            <CustomerContractDeliveryDialog
                id={selectedContract && selectedContract.contractNumber}
                companyName={selectedContract && selectedContract.companyName}
                onCloseClick={handleDialogClose}
                onSubmitClick={() => handleOnSubmit(selectedContract, invoiceDocumentFile, signatureRef ? signatureRef.toDataURL() : null)}
                showDialog={openDialog}
                setInvoiceDocument={setInvoiceDocument}
                setSignatureRef={setSignatureRef}
                onDialogClose={handleDialogClose}
                role={auth.role}
                contract={selectedContract}
            />

        </PageWrapper>
    );
}

CustomerContracts.propTypes = {};
CustomerContracts.defaultProps = {};

const mapStateToProps = state => ({
    auth: state.auth,
    user: state.user,
    contracts: state.contracts
});

const mapDispatchToProp = {
    getAllContracts,
    getCustomerContracts,
    getCompanyContracts,
    updateContract,
    agreementSignedEvent,
};

export default connect(mapStateToProps, mapDispatchToProp)(CustomerContracts);
